// TrackedItemRow primitive: notes field.
//
// Notes textarea with an explicit Save button — matches the analyst
// criteria-checklist modal pattern (explicit Save, not auto-save). The
// Save button is enabled only while the buffer differs from the persisted
// value. Captured in audit by the consumer's notes-update endpoint.

import { useEffect, useState } from "react";
import { Button } from "@/design-system/components/ui";
import { Textarea } from "@/components/ui/Select";

interface TrackedItemNotesFieldProps {
  value: string;
  onSave: (notes: string) => void;
  pending?: boolean;
  disabled?: boolean;
  rows?: number;
  label?: string;
  /** Max length enforced client-side; server enforces 2000. */
  maxLength?: number;
}

export function TrackedItemNotesField({
  value,
  onSave,
  pending = false,
  disabled = false,
  rows = 3,
  label = "Notes",
  maxLength = 2000,
}: TrackedItemNotesFieldProps) {
  const [buffer, setBuffer] = useState(value);

  // Re-sync the buffer whenever the persisted value changes (e.g. after a
  // successful save invalidates the query).
  useEffect(() => {
    setBuffer(value);
  }, [value]);

  const dirty = buffer !== value;

  return (
    <div className="flex flex-col gap-space-2">
      <label className="font-display text-label uppercase tracking-label text-gold-shadow">
        {label}
      </label>
      <Textarea
        value={buffer}
        rows={rows}
        maxLength={maxLength}
        disabled={disabled || pending}
        placeholder="Add context, follow-ups, or counterparty notes…"
        onChange={(e) => setBuffer(e.target.value)}
        aria-label={label}
      />
      <div className="flex justify-end">
        <Button
          variant="secondary"
          size="sm"
          disabled={disabled || pending || !dirty}
          onClick={() => onSave(buffer)}
        >
          {pending ? "Saving…" : dirty ? "Save notes" : "Saved"}
        </Button>
      </div>
    </div>
  );
}
